import type { MapDefinition, PathCell } from '../../model/gamestate/GameState';
import type { PathTileAtlasId } from '../pathTileRegistry';
import { findAtlasLayer, isWithinMap, supportsSquarePathAutotiling } from './pathTileState';
import type {
  NeighborRetilePatchEntry,
  PathAutotiler,
  PathTileNeighborStatus,
  PathTileRenderTile,
  PathTileRecommendation,
} from './PathAutotiler';

interface PathTileVariantDefinition {
  readonly tileIndex: number;
  readonly signature: PathTileNeighborStatus;
}

const ROTATION_STEPS_BY_QUARTER_TURN = 3;

const BASE_VARIANTS: readonly PathTileVariantDefinition[] = [
  {
    tileIndex: 13,
    signature: {
      north: false,
      east: false,
      south: false,
      west: false,
      northWest: false,
      northEast: false,
      southEast: false,
      southWest: false,
    },
  },
  {
    tileIndex: 11,
    signature: {
      north: false,
      east: true,
      south: false,
      west: false,
      northWest: false,
      northEast: false,
      southEast: false,
      southWest: false,
    },
  },
  {
    tileIndex: 7,
    signature: {
      north: true,
      east: false,
      south: true,
      west: false,
      northWest: false,
      northEast: false,
      southEast: false,
      southWest: false,
    },
  },
  {
    tileIndex: 12,
    signature: {
      north: true,
      east: false,
      south: false,
      west: true,
      northWest: false,
      northEast: false,
      southEast: false,
      southWest: false,
    },
  },
  {
    tileIndex: 6,
    signature: {
      north: true,
      east: false,
      south: false,
      west: true,
      northWest: true,
      northEast: false,
      southEast: false,
      southWest: false,
    },
  },
  {
    tileIndex: 8,
    signature: {
      north: true,
      east: false,
      south: false,
      west: true,
      northWest: true,
      northEast: false,
      southEast: false,
      southWest: false,
    },
  },
  {
    tileIndex: 1,
    signature: {
      north: true,
      east: true,
      south: false,
      west: true,
      northWest: false,
      northEast: false,
      southEast: false,
      southWest: false,
    },
  },
  {
    tileIndex: 0,
    signature: {
      north: true,
      east: true,
      south: false,
      west: true,
      northWest: false,
      northEast: true,
      southEast: false,
      southWest: false,
    },
  },
  {
    tileIndex: 5,
    signature: {
      north: true,
      east: true,
      south: false,
      west: true,
      northWest: true,
      northEast: true,
      southEast: false,
      southWest: false,
    },
  },
  {
    tileIndex: 2,
    signature: {
      north: true,
      east: true,
      south: true,
      west: true,
      northWest: false,
      northEast: true,
      southEast: false,
      southWest: true,
    },
  },
  {
    tileIndex: 3,
    signature: {
      north: true,
      east: true,
      south: true,
      west: true,
      northWest: true,
      northEast: true,
      southEast: false,
      southWest: true,
    },
  },
  {
    tileIndex: 4,
    signature: {
      north: true,
      east: true,
      south: true,
      west: true,
      northWest: true,
      northEast: true,
      southEast: true,
      southWest: true,
    },
  },
] as const;

function getPathTileNeighborStatus(
  map: MapDefinition,
  atlasId: PathTileAtlasId,
  x: number,
  y: number,
): PathTileNeighborStatus | null {
  if (!supportsSquarePathAutotiling(map, atlasId) || !isWithinMap(map, x, y)) {
    return null;
  }
  const layer = findAtlasLayer(map, atlasId);
  const occupied = (targetX: number, targetY: number): boolean => {
    if (targetX === x && targetY === y) {
      return true;
    }
    if (!isWithinMap(map, targetX, targetY)) {
      return false;
    }
    return Boolean(layer?.data[(targetY * map.width) + targetX]);
  };
  return {
    north: occupied(x, y - 1),
    east: occupied(x + 1, y),
    south: occupied(x, y + 1),
    west: occupied(x - 1, y),
    northWest: occupied(x - 1, y - 1),
    northEast: occupied(x + 1, y - 1),
    southEast: occupied(x + 1, y + 1),
    southWest: occupied(x - 1, y + 1),
  };
}

function recommendPathTileForNeighborStatus(signature: PathTileNeighborStatus): PathTileRecommendation {
  const mirroredHorizontalCap = resolveMirroredHorizontalCapTransform(signature);
  if (mirroredHorizontalCap) {
    return mirroredHorizontalCap;
  }
  const mirroredUpsideDownTShoulder = resolveMirroredUpsideDownTShoulderTransform(signature);
  if (mirroredUpsideDownTShoulder) {
    return mirroredUpsideDownTShoulder;
  }
  const denseIntersection = resolveDenseIntersectionTransform(signature);
  if (denseIntersection) {
    return denseIntersection;
  }
  let best: { score: number; transform: PathTileRecommendation } | null = null;
  for (let variantIndex = 0; variantIndex < BASE_VARIANTS.length; variantIndex += 1) {
    const variant = BASE_VARIANTS[variantIndex];
    for (let quarterTurns = 0; quarterTurns < 4; quarterTurns += 1) {
      for (const flipHorizontal of [false, true] as const) {
        const transformedSignature = transformSignature(variant.signature, quarterTurns, flipHorizontal);
        const score = scoreSignatureMatch(signature, transformedSignature)
          + (flipHorizontal ? 1 : 0)
          + (quarterTurns * 0.01)
          + (variantIndex * 0.0001);
        if (!best || score < best.score) {
          best = {
            score,
            transform: {
              tileIndex: variant.tileIndex,
              flipHorizontal,
              flipVertical: false,
              rotationSteps: quarterTurns * ROTATION_STEPS_BY_QUARTER_TURN,
            },
          };
        }
      }
    }
  }
  return best?.transform ?? {
    tileIndex: 13,
    flipHorizontal: false,
    flipVertical: false,
    rotationSteps: 0,
  };
}

function buildNeighborRetilePatch(
  map: MapDefinition,
  _atlasId: PathTileAtlasId,
  centerX: number,
  centerY: number,
  centerCell: PathCell | null,
): NeighborRetilePatchEntry[] {
  if (map.gridType !== 'square' || !isWithinMap(map, centerX, centerY)) {
    return [];
  }
  return [{ x: centerX, y: centerY, cell: centerCell }];
}

function resolveMirroredHorizontalCapTransform(signature: PathTileNeighborStatus): PathTileRecommendation | null {
  const hasNoDiagonals =
    !signature.northWest
    && !signature.northEast
    && !signature.southEast
    && !signature.southWest;
  if (!hasNoDiagonals || signature.north || signature.south) {
    return null;
  }
  if (signature.east && !signature.west) {
    return {
      tileIndex: 11,
      flipHorizontal: false,
      flipVertical: false,
      rotationSteps: 0,
    };
  }
  if (signature.west && !signature.east) {
    return {
      tileIndex: 11,
      flipHorizontal: true,
      flipVertical: false,
      rotationSteps: 0,
    };
  }
  return null;
}

function resolveMirroredUpsideDownTShoulderTransform(signature: PathTileNeighborStatus): PathTileRecommendation | null {
  const hasNoSouthConnections =
    !signature.south
    && !signature.southEast
    && !signature.southWest;
  if (!hasNoSouthConnections) {
    return null;
  }
  if (signature.east && signature.northEast && !signature.north && !signature.west && !signature.northWest) {
    return {
      tileIndex: 6,
      flipHorizontal: false,
      flipVertical: false,
      rotationSteps: 3,
    };
  }
  if (signature.west && signature.northWest && !signature.north && !signature.east && !signature.northEast) {
    return {
      tileIndex: 6,
      flipHorizontal: true,
      flipVertical: true,
      rotationSteps: 3,
    };
  }
  return null;
}

function resolveDenseIntersectionTransform(signature: PathTileNeighborStatus): PathTileRecommendation | null {
  if (!signature.north || !signature.east || !signature.south || !signature.west) {
    return null;
  }

  const missingNorthWest = !signature.northWest;
  const missingNorthEast = !signature.northEast;
  const missingSouthEast = !signature.southEast;
  const missingSouthWest = !signature.southWest;
  const missingCount = Number(missingNorthWest) + Number(missingNorthEast) + Number(missingSouthEast) + Number(missingSouthWest);

  if (missingCount <= 1) {
    return { tileIndex: 4, flipHorizontal: false, flipVertical: false, rotationSteps: 0 };
  }
  if (missingNorthWest && missingSouthEast && !missingNorthEast && !missingSouthWest) {
    return { tileIndex: 3, flipHorizontal: false, flipVertical: false, rotationSteps: 0 };
  }
  if (!missingNorthWest && missingNorthEast && !missingSouthEast && missingSouthWest) {
    return { tileIndex: 3, flipHorizontal: false, flipVertical: false, rotationSteps: ROTATION_STEPS_BY_QUARTER_TURN };
  }
  if (missingNorthWest && !missingNorthEast && missingSouthEast && !missingSouthWest) {
    return { tileIndex: 3, flipHorizontal: false, flipVertical: true, rotationSteps: 0 };
  }
  if (!missingNorthWest && missingNorthEast && !missingSouthEast && missingSouthWest) {
    return { tileIndex: 3, flipHorizontal: true, flipVertical: false, rotationSteps: 0 };
  }
  if (missingNorthWest && missingNorthEast && !missingSouthEast && !missingSouthWest) {
    return { tileIndex: 9, flipHorizontal: false, flipVertical: false, rotationSteps: 0 };
  }
  if (!missingNorthWest && missingNorthEast && missingSouthEast && !missingSouthWest) {
    return { tileIndex: 9, flipHorizontal: false, flipVertical: false, rotationSteps: ROTATION_STEPS_BY_QUARTER_TURN };
  }
  if (!missingNorthWest && !missingNorthEast && missingSouthEast && missingSouthWest) {
    return { tileIndex: 9, flipHorizontal: false, flipVertical: false, rotationSteps: ROTATION_STEPS_BY_QUARTER_TURN * 2 };
  }
  if (missingNorthWest && !missingNorthEast && !missingSouthEast && missingSouthWest) {
    return { tileIndex: 9, flipHorizontal: false, flipVertical: false, rotationSteps: ROTATION_STEPS_BY_QUARTER_TURN * 3 };
  }
  if (missingNorthWest && missingNorthEast && missingSouthEast && !missingSouthWest) {
    return { tileIndex: 1, flipHorizontal: false, flipVertical: false, rotationSteps: 0 };
  }
  if (!missingNorthWest && missingNorthEast && missingSouthEast && missingSouthWest) {
    return { tileIndex: 1, flipHorizontal: false, flipVertical: false, rotationSteps: ROTATION_STEPS_BY_QUARTER_TURN };
  }
  if (missingNorthWest && !missingNorthEast && missingSouthEast && missingSouthWest) {
    return { tileIndex: 1, flipHorizontal: false, flipVertical: false, rotationSteps: ROTATION_STEPS_BY_QUARTER_TURN * 2 };
  }
  if (missingNorthWest && missingNorthEast && !missingSouthEast && missingSouthWest) {
    return { tileIndex: 1, flipHorizontal: false, flipVertical: false, rotationSteps: ROTATION_STEPS_BY_QUARTER_TURN * 3 };
  }
  return null;
}

function scoreSignatureMatch(target: PathTileNeighborStatus, candidate: PathTileNeighborStatus): number {
  let score = 0;
  score += mismatchPenalty(target.north, candidate.north, 100);
  score += mismatchPenalty(target.east, candidate.east, 100);
  score += mismatchPenalty(target.south, candidate.south, 100);
  score += mismatchPenalty(target.west, candidate.west, 100);
  score += mismatchPenalty(target.northWest, candidate.northWest, 10);
  score += mismatchPenalty(target.northEast, candidate.northEast, 10);
  score += mismatchPenalty(target.southEast, candidate.southEast, 10);
  score += mismatchPenalty(target.southWest, candidate.southWest, 10);
  return score;
}

function mismatchPenalty(expected: boolean, actual: boolean, weight: number): number {
  return expected === actual ? 0 : weight;
}

function transformSignature(
  signature: PathTileNeighborStatus,
  quarterTurns: number,
  flipHorizontal: boolean,
): PathTileNeighborStatus {
  const initial = flipHorizontal ? flipSignatureHorizontally(signature) : signature;
  let next = initial;
  for (let index = 0; index < quarterTurns; index += 1) {
    next = rotateSignatureClockwise(next);
  }
  return next;
}

function rotateSignatureClockwise(signature: PathTileNeighborStatus): PathTileNeighborStatus {
  return {
    north: signature.west,
    east: signature.north,
    south: signature.east,
    west: signature.south,
    northWest: signature.southWest,
    northEast: signature.northWest,
    southEast: signature.northEast,
    southWest: signature.southEast,
  };
}

function flipSignatureHorizontally(signature: PathTileNeighborStatus): PathTileNeighborStatus {
  return {
    north: signature.north,
    east: signature.west,
    south: signature.south,
    west: signature.east,
    northWest: signature.northEast,
    northEast: signature.northWest,
    southEast: signature.southWest,
    southWest: signature.southEast,
  };
}

function buildNeighborStatus(
  layer: ReturnType<typeof findAtlasLayer>,
  map: MapDefinition,
  x: number,
  y: number,
  overrides: ReadonlyMap<string, boolean>,
): PathTileNeighborStatus {
  return {
    north: isOccupied(layer, map.width, x, y - 1, overrides),
    east: isOccupied(layer, map.width, x + 1, y, overrides),
    south: isOccupied(layer, map.width, x, y + 1, overrides),
    west: isOccupied(layer, map.width, x - 1, y, overrides),
    northWest: isOccupied(layer, map.width, x - 1, y - 1, overrides),
    northEast: isOccupied(layer, map.width, x + 1, y - 1, overrides),
    southEast: isOccupied(layer, map.width, x + 1, y + 1, overrides),
    southWest: isOccupied(layer, map.width, x - 1, y + 1, overrides),
  };
}

function isOccupied(
  layer: ReturnType<typeof findAtlasLayer>,
  mapWidth: number,
  x: number,
  y: number,
  overrides: ReadonlyMap<string, boolean>,
): boolean {
  const override = overrides.get(`${x},${y}`);
  if (typeof override === 'boolean') {
    return override;
  }
  if (!layer || x < 0 || y < 0 || x >= layer.width || y >= layer.height) {
    return false;
  }
  return Boolean(layer.data[(y * mapWidth) + x]);
}

export const legacyPathAutotiler: PathAutotiler = {
  id: 'legacy-square-mask-v1',
  recommendAt(map, atlasId, x, y) {
    const neighborStatus = getPathTileNeighborStatus(map, atlasId, x, y);
    return neighborStatus ? recommendPathTileForNeighborStatus(neighborStatus) : null;
  },
  recommendForNeighborStatus: recommendPathTileForNeighborStatus,
  buildNeighborRetilePatch,
  buildNeighborFreezePatch() {
    return [];
  },
  buildGhostBorderRetilePatch() {
    return [];
  },
  resolveRenderTile(map, atlasId, x, y, cell): PathTileRenderTile | null {
    if (!cell) {
      return null;
    }
    if (cell.mode === 'fixed') {
      return null;
    }
    const recommendation = legacyPathAutotiler.recommendAt(map, atlasId, x, y);
    if (!recommendation) {
      return null;
    }
    return {
      kind: 'atlas-cell',
      tileIndex: recommendation.tileIndex,
      flipHorizontal: recommendation.flipHorizontal,
      flipVertical: recommendation.flipVertical,
      rotationSteps: recommendation.rotationSteps,
    };
  },
};
